import { useGetManyReference } from "react-admin";
import { FieldValues, Path, useController } from "react-hook-form";
import { Instructor } from "../providers/instructorsProvider";
import { SelectInput } from "./SelectInput";
import { Autocomplete, FormControl, SxProps, TextField } from "@mui/material";
import { useAutovioContext } from "../hooks/useAutovioContext";
import { useState } from "react";

export function InstructorInput(props: {
  source: Path<FieldValues>;
  bulkEdit?: boolean;
  searchable?: boolean;
  branchId?: string;
  sx?: SxProps;
  size?: "small";
}) {
  const { source, bulkEdit, searchable, branchId, sx, size } = props;
  const [{ drivingSchoolId }] = useAutovioContext();
  const { data: instructors, isLoading } = useGetManyReference<Instructor>("instructors", {
    target: "drivingSchoolId",
    id: drivingSchoolId,
    sort: { field: "name", order: "ASC" },
    pagination: { page: 1, perPage: 999 },
    filter: { isActive: true },
  });

  if (searchable) {
    return (
      <SearchableInstructorInput
        source={source}
        instructors={instructors ?? []}
        isLoading={isLoading}
        branchId={branchId}
        sx={sx}
        size={size}
      />
    );
  }

  return (
    <SelectInput
      sx={sx}
      source={source}
      label="Fahrlehrer"
      options={instructors?.map((instructor) => [instructor.id, instructor.name])}
      bulkEdit={bulkEdit}
      validate={(value) => (value ? true : "Bitte auswählen.")}
      size={size}
    />
  );
}

function SearchableInstructorInput(props: {
  source: Path<FieldValues>;
  instructors: Instructor[];
  isLoading: boolean;
  branchId?: string;
  sx?: SxProps;
  size?: "small";
}) {
  const { source, instructors, isLoading, branchId, sx, size } = props;
  const {
    field,
    fieldState: { error },
    formState: { isSubmitting },
  } = useController({ name: source, rules: { validate: (value) => (value ? true : "Bitte auswählen.") } });
  const [searchText, setSearchText] = useState<string | null>(null);
  const selectedInstructor = instructors.find((instructor) => instructor.id === field.value) ?? null;
  const options = branchId
    ? [...instructors].sort(
        (a, b) =>
          Number(b.assignedBranches.includes(branchId)) - Number(a.assignedBranches.includes(branchId)) ||
          a.name.localeCompare(b.name, "de"),
      )
    : instructors;

  return (
    <FormControl sx={sx}>
      <Autocomplete
        size={size}
        options={options}
        groupBy={
          branchId
            ? (instructor) =>
                instructor.assignedBranches.includes(branchId) ? "Gewählte Filiale" : "Weitere Fahrlehrer"
            : undefined
        }
        slotProps={{ paper: { sx: { "& .MuiAutocomplete-groupLabel": { color: "text.secondary" } } } }}
        value={selectedInstructor}
        inputValue={searchText ?? selectedInstructor?.name ?? ""}
        onChange={(_, instructor) => {
          field.onChange(instructor?.id ?? "");
          setSearchText(null);
        }}
        onInputChange={(_, value, reason) => {
          if (reason === "input") {
            setSearchText(value);
            field.onChange("");
          } else if (reason === "clear") {
            setSearchText(null);
          }
        }}
        onBlur={() => {
          setSearchText(null);
          field.onBlur();
        }}
        disabled={isSubmitting}
        loading={isLoading}
        loadingText="Lädt..."
        noOptionsText="Keine Fahrlehrer gefunden"
        openOnFocus
        autoHighlight
        clearOnEscape
        openText="Öffnen"
        clearText="Löschen"
        closeText="Schließen"
        isOptionEqualToValue={(option, value) => option.id === value.id}
        getOptionLabel={(instructor) => instructor.name}
        getOptionKey={(instructor) => instructor.id}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Fahrlehrer"
            error={!!error}
            helperText={error?.message}
            inputRef={field.ref}
            InputLabelProps={{ required: true }}
            inputProps={{ ...params.inputProps, "aria-required": true }}
            sx={{ mt: 0, mb: 0 }}
          />
        )}
      />
    </FormControl>
  );
}
